import { SITE } from "./site";

export interface Author {
  slug: string;
  name: string;
  bio: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  avatar?: string;
  /** Absolute or root-relative URL for the author page/anchor. */
  url: string;
  /** URLs used in JSON-LD `sameAs`. */
  sameAs: string[];
  isDefault?: boolean;
}

export interface AuthorRow {
  id: string;
  slug: string;
  name: string;
  bio: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  website_url: string | null;
  avatar_url: string | null;
  is_default: boolean;
}

export function toAuthor(row: AuthorRow): Author {
  const sameAs = [row.linkedin_url, row.twitter_url, row.website_url].filter(
    (u): u is string => Boolean(u),
  );
  return {
    slug: row.slug,
    name: row.name,
    bio: row.bio ?? "",
    linkedin: row.linkedin_url ?? undefined,
    twitter: row.twitter_url ?? undefined,
    website: row.website_url ?? undefined,
    avatar: row.avatar_url ?? undefined,
    url: `${SITE.url}/about#${row.slug}`,
    sameAs,
    isDefault: row.is_default,
  };
}

/**
 * Resolve an author by the free-text `author_name` stored on `articles`,
 * falling back to the default author only when no name is set.
 */
export function resolveAuthor(
  name: string | null | undefined,
  authors: AuthorRow[] | null | undefined,
): Author | null {
  const rows = authors ?? [];
  if (rows.length === 0) return null;
  const needle = (name ?? "").trim().toLowerCase();
  // Only fall back to the default author when no name was saved. An
  // unknown name (e.g. a guest contributor) must keep its own byline.
  const row = needle
    ? rows.find((r) => r.name.trim().toLowerCase() === needle)
    : rows.find((r) => r.is_default) ?? rows[0];
  return row ? toAuthor(row) : null;
}

export function getDefaultAuthor(authors: AuthorRow[] | null | undefined): Author | null {
  const rows = authors ?? [];
  const row = rows.find((r) => r.is_default) ?? rows[0];
  return row ? toAuthor(row) : null;
}

export function getDefaultAuthorName(authors: AuthorRow[] | null | undefined): string {
  return getDefaultAuthor(authors)?.name ?? "Editorial team";
}
